import { useContext, useMemo } from "react";
import { useTranslation } from "next-i18next";
import { FeedCardContext } from "src/components/feed-card/feed-card";
import { IFeedCardUserReactionBody } from "src/components/feed-card/types/content";
import { EnumFeedClickTrackingArea, useFeedClickTracking } from "src/components/pages/feed/hooks/use-feed-click-tracking";
import { TUserReactionValuesWithConfig } from "src/components/user-reactions/types";
import { UserReactionBar } from "src/components/user-reactions/user-reaction-bar";
import { UserReactionBarLabel } from "src/components/user-reactions/user-reaction-bar-label";
import { getConfigForReactionType } from "src/components/user-reactions/utils";

const HIGHEST_REACTION_VALUE = 1;

interface IFeedCardUserReactionContentProps extends IFeedCardUserReactionBody {
    onReactionBarClick?: () => void;
    onReactionUnderlyingLinkClick?: (underlyingUrl: string) => void;
}

export const FeedCardUserReactionContent = ({
    reactionType,
    reactionValue,
    underlyingIsin,
    underlyingName,
    url,
    onReactionBarClick,
    onReactionUnderlyingLinkClick,
}: IFeedCardUserReactionContentProps) => {
    const { t } = useTranslation("reactions");
    const config = getConfigForReactionType(reactionType, t);
    const mappedReactionValues: TUserReactionValuesWithConfig | undefined = useMemo(
        () =>
            config?.options.map(option => ({
                ...option,
                numberOfReactions: option.value === reactionValue ? 1 : 0,
            })),
        [config?.options, reactionValue]
    );

    return (
        mappedReactionValues &&
        config && (
            <UserReactionBar
                label={
                    <UserReactionBarLabel
                        label={config.label}
                        underlyingIsin={underlyingIsin}
                        underlyingName={underlyingName}
                        onLinkClick={(underlyingUrl: string) => onReactionUnderlyingLinkClick?.(underlyingUrl)}
                    />
                }
                reactionValues={mappedReactionValues}
                highestReactionValue={HIGHEST_REACTION_VALUE}
                selectedValue={String(reactionValue)}
                isPreview
                isPreviewClickable={!!url}
                onClick={onReactionBarClick}
            />
        )
    );
};

export const FeedCardUserReaction = ({ reactionType, reactionValue, underlyingIsin, underlyingName, url }: IFeedCardUserReactionBody) => {
    const context = useContext(FeedCardContext);
    const { trackFeedClick } = useFeedClickTracking();
    const handleUserReactionBarClick = () => {
        if (url) {
            trackFeedClick(context.contentId, url, EnumFeedClickTrackingArea.Body);
            window.open(url, "_blank");
        }
    };

    const handleUserReactionUnderlyingLinkClick = (underlyingUrl: string) => {
        // adding the origin because underlyingUrl built by the linkBuilder is relative
        trackFeedClick(context.contentId, `${window.location.origin}${underlyingUrl}`, EnumFeedClickTrackingArea.Body);
    };

    return (
        <FeedCardUserReactionContent
            reactionType={reactionType}
            reactionValue={reactionValue}
            underlyingIsin={underlyingIsin}
            underlyingName={underlyingName}
            url={url}
            onReactionBarClick={handleUserReactionBarClick}
            onReactionUnderlyingLinkClick={handleUserReactionUnderlyingLinkClick}
        />
    );
};
